import React from 'react';
import { PageView } from '../types';
import { ArrowUpRight, Linkedin, Calendar } from 'lucide-react';
import { FOUNDER_CONFIG } from '../config/conference';

interface FooterProps {
  setActivePage: (page: PageView) => void;
  onOpenLeadModal: () => void;
}

export const Footer: React.FC<FooterProps> = ({ setActivePage, onOpenLeadModal }) => {
  const handleNav = (page: PageView) => {
    setActivePage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="minimal-footer">
      <div className="section-container footer-inner">
        <div className="footer-left">
          <div className="footer-logo">
            <img src="/favicon.svg" alt="MUSE Telemetry Logo" className="brand-logo-img" />
            <span className="logo-title">MUSE TELEMETRY</span>
          </div>
          <p className="footer-desc">
            The vendor-independent intelligence layer for modern network operations. Unifying telemetry, automated event clustering, and customer-edge visibility across mixed-vendor ISP networks.
          </p>
          <span className="copyright-text">
            © {new Date().getFullYear()} Muse Umbrella LLC. All rights reserved.
          </span>
        </div>

        <div className="footer-links-grid">
          <div className="footer-col">
            <span className="col-heading">Navigation</span>
            <button onClick={() => handleNav('home')}>Overview</button>
            <button onClick={() => handleNav('features')}>Capabilities</button>
            <button onClick={() => handleNav('why-muse')}>Philosophy</button>
            <button onClick={() => handleNav('blog')}>Articles</button>
          </div>

          <div className="footer-col">
            <span className="col-heading">Solutions</span>
            <button onClick={() => handleNav('consolidation')}>Consolidation</button>
            <button onClick={() => handleNav('ops')}>Outage Response</button>
            <button onClick={() => handleNav('forge')}>Forge Sandbox</button>
          </div>

          <div className="footer-col">
            <span className="col-heading">Platform &amp; Connect</span>
            <a href={FOUNDER_CONFIG.bookingUrl} target="_blank" rel="noopener noreferrer">
              <Calendar size={13} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} />
              <span>Schedule 15-Min Call</span>
              <ArrowUpRight size={12} />
            </a>
            <button onClick={onOpenLeadModal}>Book 15-Min Review Form</button>
            <a href="https://demo.musetelemetry.com" target="_blank" rel="noopener noreferrer">
              Live Demo <ArrowUpRight size={12} />
            </a>
            <a href={FOUNDER_CONFIG.linkedinUrl} target="_blank" rel="noopener noreferrer">
              <Linkedin size={13} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} />
              <span>Connect on LinkedIn</span>
              <ArrowUpRight size={12} />
            </a>
          </div>
        </div>
      </div>

      <style>{`
        .minimal-footer {
          background: #000000;
          border-top: 1px solid var(--border-color);
          padding: 3rem 0 2.5rem;
          margin-top: 3rem;
        }

        .footer-inner {
          display: flex;
          justify-content: space-between;
          gap: 4rem;
        }

        .footer-left {
          max-width: 420px;
        }

        .footer-logo {
          display: flex;
          align-items: center;
          gap: 0.65rem;
          margin-bottom: 1rem;
        }

        .brand-logo-img {
          width: 24px;
          height: 24px;
          border-radius: 6px;
          display: block;
        }

        .logo-title {
          font-weight: 700;
          font-size: 0.9rem;
          color: #ffffff;
        }

        .footer-desc {
          color: var(--text-muted);
          font-size: 0.85rem;
          line-height: 1.5;
          margin-bottom: 1.5rem;
        }

        .copyright-text {
          font-size: 0.78rem;
          color: var(--text-dim);
          display: block;
        }

        .footer-links-grid {
          display: flex;
          gap: 3.5rem;
        }

        .footer-col {
          display: flex;
          flex-direction: column;
          gap: 0.65rem;
        }

        .col-heading {
          font-size: 0.78rem;
          font-weight: 600;
          color: #ffffff;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 0.5rem;
        }

        .footer-col button, .footer-col a {
          background: none;
          border: none;
          color: var(--text-muted);
          font-size: 0.85rem;
          cursor: pointer;
          text-align: left;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0;
          transition: color 0.15s;
        }

        .footer-col button:hover, .footer-col a:hover {
          color: #ffffff;
        }

        @media (max-width: 750px) {
          .footer-inner {
            flex-direction: column;
            gap: 2.5rem;
          }
          .footer-links-grid {
            flex-wrap: wrap;
            gap: 2.5rem;
          }
        }
      `}</style>
    </footer>
  );
};

export default Footer;
